import { createClient } from '@supabase/supabase-js';
import { ErrorHandler, ErrorCategories, ErrorSeverity, withErrorHandling } from '../utils/errorHandling';
import { optimizeQuery } from '../utils/performance';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'X-Client-Info': 'manivo-web-app'
    }
  }
});

// Types for our database tables
export interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  bio: string | null;
  timezone: string;
  notification_preferences: {
    email: boolean;
    push: boolean;
    daily_reminders: boolean;
    manifestation_updates?: boolean;
    library_updates?: boolean;
  };
  app_preferences: {
    theme: string;
    language: string;
    autoSave: boolean;
    soundEffects: boolean;
    vibration: boolean;
    dailyGoal: number;
  };
  privacy_preferences: {
    profileVisibility: string;
    manifestationSharing: boolean;
    dataCollection: boolean;
    analytics: boolean;
  };
  consecutive_login_days: number | null;
  last_login_date: string | null;
  commitment_tier: string | null;
  created_at: string;
  updated_at: string;
}

export interface ManifestationCategory {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
}

export interface DailyManifestation {
  id: string;
  category_id: string;
  manifestation_text: string;
  created_at: string;
}

export interface UserManifestationSubscription {
  id: string;
  user_id: string;
  category_id: string;
  is_active: boolean;
  created_at: string;
}

export interface UserDailyManifestation {
  id: string;
  user_id: string;
  manifestation_id: string;
  delivered_at: string;
  delivered_date: string;
  is_read: boolean;
}

export interface UserManifestation {
  id: string;
  user_id: string;
  manifestation_text: string;
  status: string;
  created_at: string;
  updated_at: string;
}

// Helper function to calculate consecutive login days
const calculateConsecutiveLoginDays = (lastLoginDate: string | null): number => {
  if (!lastLoginDate) return 1; // First login
  
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const lastLogin = new Date(lastLoginDate);
  const todayStr = today.toISOString().split('T')[0];
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  const lastLoginStr = lastLogin.toISOString().split('T')[0];
  
  if (lastLoginStr === todayStr) {
    // Already logged in today, don't change streak
    return -1; // Special value to indicate no change
  } else if (lastLoginStr === yesterdayStr) {
    // Logged in yesterday, increment streak
    return 1; // Will be added to current streak
  } else {
    // Gap in login, reset streak
    return 1; // Reset to 1
  }
};

// Auth helper functions
export const signUp = withErrorHandling(async (email: string, password: string, fullName: string) => {
  const query = optimizeQuery('signUp', [email, fullName]);
  
  return query.execute(async () => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/verify-email`,
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) throw error;

    // Create user profile with initial values
    if (data.user) {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: data.user.id,
          full_name: fullName,
          consecutive_login_days: 0,
          last_login_date: null,
          commitment_tier: 'seeker',
        }, {
          onConflict: 'user_id'
        });

      if (profileError) throw profileError;
    }

    return data;
  });
}, { component: 'auth', action: 'signUp' });

export const verifyEmail = async (token: string, email: string) => {
  const { data, error } = await supabase.auth.verifyOtp({
    token,
    type: 'email',
    email
  });

  if (error) throw error;
  return data;
};

export const signIn = withErrorHandling(async (email: string, password: string) => {
  const query = optimizeQuery('signIn', [email]);
  
  return query.execute(async () => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    // Update login streak after successful sign-in (non-blocking)
    if (data.user) {
      // Don't await this - let it run in background
      updateLoginStreak(data.user.id).catch(streakError => {
        // Non-critical error, can be ignored
        ErrorHandler.getInstance().handleError(
          streakError as Error,
          { component: 'auth', action: 'updateLoginStreak', userId: data.user.id },
          ErrorSeverity.LOW,
          ErrorCategories.DATABASE
        );
      });
    }

    return data;
  });
}, { component: 'auth', action: 'signIn' });

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
  
  // Clear any local storage items related to user state
  localStorage.removeItem('manivo_subscription_info');
  localStorage.removeItem('dana_orb_dismissed_today');
  localStorage.removeItem('dana_orb_last_seen');
  
  return true;
};

export const resetPassword = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

export const getCurrentUser = withErrorHandling(async () => {
  const query = optimizeQuery('getCurrentUser', []);
  
  return query.execute(async () => {
    const { data: { user } } = await supabase.auth.getUser();
  
    if (user) {
      try {
        // Fetch user profile data
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error) {
          // If profile doesn't exist, return user without profile for now
          // Profile will be created on login streak update
          if (error.code === 'PGRST116') {
            ErrorHandler.getInstance().handleError(
              new Error('User profile not found'),
              { component: 'auth', action: 'getCurrentUser', userId: user.id },
              ErrorSeverity.LOW,
              ErrorCategories.DATABASE
            );
            return user;
          }
          ErrorHandler.getInstance().handleError(
            new Error(error.message),
            { component: 'auth', action: 'fetchUserProfile', userId: user.id },
            ErrorSeverity.MEDIUM,
            ErrorCategories.DATABASE
          );
          return user;
        }

        // Attach profile data to user object
        return {
          ...user,
          profile: profile as UserProfile
        };
      } catch (error) {
        return user;
      }
    }
  
    return user;
  });
}, { component: 'auth', action: 'getCurrentUser' });

// Update login streak
export const updateLoginStreak = withErrorHandling(async (userId: string) => {
  const query = optimizeQuery('updateLoginStreak', [userId]);
  
  return query.execute(async () => {
    try {
      // Get current profile
      const { data: profile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('consecutive_login_days, last_login_date')
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        // If profile doesn't exist, create it first
        if (fetchError.code === 'PGRST116') {
          const { error: createError } = await supabase
            .from('user_profiles')
            .upsert({
              user_id: userId,
              full_name: null,
              bio: null,
              timezone: 'UTC',
              notification_preferences: {
                email: true,
                push: true,
                daily_reminders: true
              },
              consecutive_login_days: 1,
              last_login_date: new Date().toISOString().split('T')[0],
              commitment_tier: 'seeker'
            }, {
              onConflict: 'user_id'
            });
          
          if (createError) {
            throw createError;
          }
          return;
        }
        throw fetchError;
      }

      const today = new Date().toISOString().split('T')[0];
      const streakChange = calculateConsecutiveLoginDays(profile.last_login_date);
      
      let newConsecutiveDays = profile.consecutive_login_days || 0;
      
      if (streakChange === -1) {
        // Already logged in today, no change
        return;
      } else if (streakChange === 1 && profile.last_login_date) {
        // Increment streak
        newConsecutiveDays = (profile.consecutive_login_days || 0) + 1;
      } else {
        // Reset streak to 1
        newConsecutiveDays = 1;
      }

      // Update profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          consecutive_login_days: newConsecutiveDays,
          last_login_date: today,
        })
        .eq('user_id', userId);

      if (updateError) throw updateError;
    } catch (error) {
      throw error;
    }
  });
}, { component: 'auth', action: 'updateLoginStreak' }, { maxRetries: 2 });

// Update user profile
export const updateUserProfile = withErrorHandling(async (userId: string, profileData: Partial<UserProfile>) => {
  const query = optimizeQuery('updateUserProfile', [userId]);
  
  return query.execute(async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...profileData,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data as UserProfile;
  });
}, { component: 'profile', action: 'updateUserProfile' });

// Update user commitment tier
export const updateUserCommitmentTier = async (userId: string, tier: string) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ commitment_tier: tier })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as UserProfile;
};

// User Manifestation helper functions
export const saveUserManifestation = withErrorHandling(async (userId: string, manifestationText: string, status: string = 'active') => {
  const query = optimizeQuery('saveUserManifestation', [userId, status]);
  
  return query.execute(async () => {
    const { data, error } = await supabase
      .from('user_manifestations')
      .insert({
        user_id: userId,
        manifestation_text: manifestationText,
        status: status,
      })
      .select()
      .single();

    if (error) throw error;
    return data as UserManifestation;
  });
}, { component: 'manifestation', action: 'saveUserManifestation' });

export const getUserManifestations = withErrorHandling(async (userId: string) => {
  const query = optimizeQuery('getUserManifestations', [userId]);
  
  return query.execute(async () => {
    const { data, error } = await supabase
      .from('user_manifestations')
      .select('*')
      .eq('user_id', userId)
      .neq('status', 'deleted') // Don't show deleted manifestations
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as UserManifestation[];
  });
}, { component: 'manifestation', action: 'getUserManifestations' });

export const updateUserManifestationStatus = async (userId: string, manifestationId: string, status: string) => {
  const { data, error } = await supabase
    .from('user_manifestations')
    .update({ status })
    .eq('id', manifestationId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as UserManifestation;
};

// Manifestation helper functions
export const getManifestationCategories = withErrorHandling(async () => {
  const query = optimizeQuery('getManifestationCategories', []);
  
  return query.execute(async () => {
    const { data, error } = await supabase
      .from('manifestation_categories')
      .select('*')
      .order('name');

    if (error) throw error;
    return data as ManifestationCategory[];
  });
}, { component: 'manifestation', action: 'getManifestationCategories' });

export const getUserSubscriptions = withErrorHandling(async (userId: string) => {
  const query = optimizeQuery('getUserSubscriptions', [userId]);
  
  return query.execute(async () => {
    const { data, error } = await supabase
      .from('user_manifestation_subscriptions')
      .select(`
        *,
        manifestation_categories (*)
      `)
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) throw error;
    return data;
  });
}, { component: 'manifestation', action: 'getUserSubscriptions' });

export const subscribeToCategory = async (userId: string, categoryId: string) => {
  const { data, error } = await supabase
    .from('user_manifestation_subscriptions')
    .upsert({
      user_id: userId,
      category_id: categoryId,
      is_active: true,
    });

  if (error) throw error;
  return data;
};

export const unsubscribeFromCategory = async (userId: string, categoryId: string) => {
  const { data, error } = await supabase
    .from('user_manifestation_subscriptions')
    .update({ is_active: false })
    .eq('user_id', userId)
    .eq('category_id', categoryId);

  if (error) throw error;
  return data;
};

// Check if user has a manifestation available for today
export const checkDailyManifestationAvailable = async (userId: string) => {
  const { data, error } = await supabase
    .rpc('get_daily_manifestation_for_user', { user_uuid: userId });

  if (error) throw error;
  return data && data.length > 0;
};

// Get today's manifestation for user (if available)
export const getDailyManifestationForUser = async (userId: string) => {
  const { data, error } = await supabase
    .rpc('get_daily_manifestation_for_user', { user_uuid: userId });

  if (error) throw error;
  return data[0] || null;
};

// Check if user has already received today's manifestation
export const hasReceivedTodaysManifestation = async (userId: string) => {
  const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD format
  
  const { data, error } = await supabase
    .from('user_daily_manifestations')
    .select('*')
    .eq('user_id', userId)
    .eq('delivered_date', today)
    .eq('is_read', true)
    .limit(1);

  if (error) throw error;
  return data && data.length > 0;
};

// Get today's delivered but unread manifestation
export const getTodaysUnreadManifestation = async (userId: string) => {
  const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD format
  
  const { data, error } = await supabase
    .from('user_daily_manifestations')
    .select(`
      *,
      daily_manifestations (
        manifestation_text,
        manifestation_categories (
          name,
          color
        )
      )
    `)
    .eq('user_id', userId)
    .eq('delivered_date', today)
    .eq('is_read', false)
    .limit(1);

  if (error) throw error;
  
  if (data && data.length > 0) {
    const record = data[0];
    return {
      manifestation_id: record.manifestation_id,
      manifestation_text: record.daily_manifestations.manifestation_text,
      category_name: record.daily_manifestations.manifestation_categories.name,
      category_color: record.daily_manifestations.manifestation_categories.color,
    };
  }
  
  return null;
};

export const markManifestationAsDelivered = async (userId: string, manifestationId: string) => {
  const { data, error } = await supabase
    .from('user_daily_manifestations')
    .insert({
      user_id: userId,
      manifestation_id: manifestationId,
      is_read: false,
    });

  if (error) throw error;
  return data;
};

export const markManifestationAsRead = async (userId: string, manifestationId: string) => {
  const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD format
  
  const { data, error } = await supabase
    .from('user_daily_manifestations')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('manifestation_id', manifestationId)
    .eq('delivered_date', today);

  if (error) throw error;
  return data;
};